import { Input, Label } from 'components';
import { ImageTranslationInput } from 'components/ImageTranslationInput';
import { SubmitBlockInput } from 'data/generated';
import { TranslationsHelper } from 'hooks/useTranslations';
import { FaPlus, FaTrash } from 'react-icons/fa';
import styled, { useTheme } from 'styled-components';

export const PavingSubmitBlock = ({
    submitBlock,
    setSubmitBlock,
    translationsHelper,
}: {
    submitBlock: SubmitBlockInput;
    setSubmitBlock: (value: SubmitBlockInput) => void;
    translationsHelper: TranslationsHelper;
}) => {
    const theme = useTheme();

    const { imageTranslationsHook } = translationsHelper;
    const { deleteImageTranslation } = imageTranslationsHook;

    const { numberOfColumns, numberOfRows, pavingImageKeys, pavingBottonImageKey } = submitBlock;

    if (!pavingImageKeys) {
        throw Error('Some params are not defined in PavingSubmitBlock');
    }

    return (
        <Container>
            <Input
                label="Number of rows"
                value={numberOfRows ?? 0}
                type="number"
                onChange={(value) => setSubmitBlock({ ...submitBlock, numberOfRows: Number(value) })}
            />
            <Input
                label="Number of columns"
                value={numberOfColumns ?? 0}
                type="number"
                onChange={(value) => setSubmitBlock({ ...submitBlock, numberOfColumns: Number(value) })}
            />
            <ImageTranslationInput
                label="Bottom Image"
                sublabel="Image that will be displayed at the bottom of the paving"
                onChange={(value) => setSubmitBlock({ ...submitBlock, pavingBottonImageKey: value })}
                value={pavingBottonImageKey ?? ''}
                translationsHelper={{
                    ...translationsHelper,
                    translationsKeyPrefix: `${translationsHelper.translationsKeyPrefix}.bottom.image`,
                }}
            />
            <Label>Images</Label>
            {pavingImageKeys.map((imageKey, index) => {
                return (
                    <InputWithImage key={pavingImageKeys.length + index}>
                        <InputsContainer>
                            <ImageTranslationInput
                                label="Image"
                                onChange={(newImageKey) =>
                                    setSubmitBlock({
                                        ...submitBlock,
                                        pavingImageKeys: pavingImageKeys.map((imageKey, imageItemIndex) =>
                                            index === imageItemIndex ? newImageKey : imageKey
                                        ),
                                    })
                                }
                                value={imageKey}
                                translationsHelper={{
                                    ...translationsHelper,
                                    translationsKeyPrefix: `${translationsHelper.translationsKeyPrefix}.${index}.image`,
                                }}
                            />
                        </InputsContainer>
                        <ButtonContainer
                            backgroundColor={theme.colors.backgroundError}
                            onClick={() => {
                                setSubmitBlock({
                                    ...submitBlock,
                                    pavingImageKeys: pavingImageKeys.filter(
                                        (_, imageItemIndex) => imageItemIndex !== index
                                    ),
                                });
                                deleteImageTranslation(imageKey);
                            }}
                        >
                            <FaTrash color="white" />
                        </ButtonContainer>
                    </InputWithImage>
                );
            })}
            <Row>
                <ButtonContainer
                    backgroundColor={theme.colors.backgroundSecondary}
                    onClick={() =>
                        setSubmitBlock({
                            ...submitBlock,
                            pavingImageKeys: [...pavingImageKeys, ''],
                        })
                    }
                >
                    <FaPlus color="white" />
                </ButtonContainer>
            </Row>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const InputWithImage = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    border-left: 7px solid #222222;
    border-radius: 7px;
    padding-left: 5px;
`;

const InputsContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 5px;
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 5px;
`;

const ButtonContainer = styled.div<{ backgroundColor: string }>`
    width: 50px;
    height: 50px;
    border-radius: 25px;
    flex-shrink: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${({ backgroundColor }) => backgroundColor};
`;
